import {
  Directive,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {ModulesService} from '../../services/modules.service';
import {BehaviorSubject, firstValueFrom, interval, Observable, of, Subject} from 'rxjs';
import {IModule} from '../../models/module/IModule';
import {catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {GlobalSearchService, GlobalSearchResult, GroupedSearchResults} from '../../services/global-search.service';
import {UnavailableModule} from '../../models/search/module-search.model';
import {RealtimeEvent, RealtimeService} from '../../core/realtime/realtime.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UserService} from '../../services/user.service';
import {PermissionsService} from '../../services/permissions.service';
import {UserVM} from '../../models/user/user-vm.model';
import {CorporateFormsService} from '../../corporate/services/corporate-forms.service';
import {GlobalRequestDialogService} from '../../global-requests/services/global-request-dialog.service';
import {UserCompany} from '../../models/user/user-companies-response-dto.model';
import {CompaniesService} from '../../services/companies.service';
import {Modules} from '../../enums/modules.enum';
import {NavigationEnd, Router} from '@angular/router';
import {FormTypeEnum} from '../../corporate/enums/form-type.enum';
import {ReferralKind, referralKindToParam} from '../../corporate/enums/referral-kind.enum';
import { ConfirmDialogComponent } from 'src/app/corporate/confirm-dialog/confirm-dialog.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import {RedirectHandlerService} from '../../auth/services/test/redirect-handler.service';
import {OrgScopeStore} from '../../services/org-scope-store';
import {MODULE_FLAG_META} from '../../permissions/read-only-flags';
import {ORG_ARRIVAL_PARAM, offerOtherOrgTab, openInOrgTab, openInOtherOrgTab} from '../../shared/navigation/org-tab';
import {orgSwitcher} from '../../shared/org-identity/org-switch.service';
import {MeetingNotesService} from '../../corporate/meeting-notes/services/meeting-notes.service';
import {MeetingNotesPanelService} from '../../corporate/meeting-notes/services/meeting-notes-panel.service';
import {ReadOnlyService} from '../../services/read-only.service';
import {canSeeGeneralNotes} from '../../permissions/notes-access';
import {PhonebookSidebarService} from '../../corporate/phonebook';
import {OutlookPanelService} from '../../corporate/outlook';
import {AIChatPanelService} from '../../ai-chat/ai-chat-panel.service';
import { NotificationVM, enrichNotification, isPendingChangeNotification, taskListLink } from '../../notifications/models/notification.model';
import { NotificationPresenterService } from '../../notifications/services/notification-presenter.service';
import { NotificationPreferencesService } from '../../notifications/services/notification-preferences.service';
import { WebPushService } from '../../notifications/services/web-push.service';
import {NgxPermissionsService} from 'ngx-permissions';

/**
 * The app shell's behaviour: the menu per module, the global search and its scope filters, the
 * company picker, notifications, module switching and the account entries. It has no template of
 * its own. BcAppShellComponent (menu/app-shell) extends it and draws the Option D layout.
 */
@Directive()
export abstract class HeaderComponent implements OnInit {

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    if (this.isSettingsOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isSettingsOpen = false;
    }
    if (this.isModuleMenuOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isModuleMenuOpen = false;
    }
    if (this.isMobileMenuOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isMobileMenuOpen = false;
    }
    if ((this.showSearchFilters || this.showSearchResults) && !this.isClickInsideSearch(event)) {
      this.closeSearchPanel();
    }
  }

  /** Escape dismisses the search panel, the same as every overlay in the app. */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeSearchPanel();
  }

  /**
   * Leaving the window (another tab, another app, a popped-out org tab) should not leave a panel
   * hanging over the page for whenever the user comes back. Material menus have no such handling
   * of their own, so close them here alongside the search panel.
   */
  @HostListener('window:blur')
  onWindowBlur(): void {
    this.closeSearchPanel();
    this.closeOpenMenus();
  }

  @ViewChild('companyFilter') companyFilter: ElementRef<HTMLInputElement>;
  /** The search box plus its dropdown - used to tell an outside click from an inside one. */
  @ViewChild('searchContainer') searchContainer?: ElementRef<HTMLElement>;
  /** Every menu the header owns (nav dropdowns, company picker, search scope), open or not. */
  @ViewChildren(MatMenuTrigger) menuTriggers?: QueryList<MatMenuTrigger>;
  /** Used to restore the caret after the company-scope menu closes. */
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  @Output()
  changeModule = new EventEmitter<number>();

  @Output()
  changeMenu = new EventEmitter();

  @Output()
  managedCompanyChange = new EventEmitter<UserCompany>();

  @Output()
  logout = new EventEmitter<void>();

  @Input()
  loginDisplay: boolean;

  @Input()
  menu: any[] = [];

  public currentUser: UserVM;
  public selectedModuleName$!: BehaviorSubject<string>;
  public activeNavItem: string = '';
  public navDropdownOpen = false;
  public navDropdownItem: any = null;

  private modulesService = inject(ModulesService);
  private userService = inject(UserService);
  private corporatePermissionsService = inject(PermissionsService);
  private formsService = inject(CorporateFormsService);
  protected companiesService = inject(CompaniesService);
  private router = inject(Router);
  private readonly globalRequestDialog = inject(GlobalRequestDialogService);
  private readonly meetingNoteService = inject(MeetingNotesService);
  public readonly meetingNotesPanelService = inject(MeetingNotesPanelService);
  /** No Notes button for a Corporate Viewer, or for a tenant that keeps Client Profile Notes only
   *  — see canSeeGeneralNotes. Reactive: the flags land after sign-in. */
  public readonly canSeeNotes$ = inject(ReadOnlyService).readOnlyIn$(Modules.Corporate)
    .pipe(map(() => canSeeGeneralNotes()));
  public readonly phonebookSidebarService = inject(PhonebookSidebarService);
  public readonly outlookPanelService = inject(OutlookPanelService);
  public readonly aiChatPanelService = inject(AIChatPanelService);
  private readonly eRef = inject(ElementRef);
  private readonly realtime = inject(RealtimeService);
  private readonly notificationPresenter = inject(NotificationPresenterService);
  private readonly notificationPreferences = inject(NotificationPreferencesService);
  private readonly webPush = inject(WebPushService);
  private readonly destroy = inject(DestroyRef);
  private readonly globalSearchService = inject(GlobalSearchService);
  private readonly permissionsService = inject(NgxPermissionsService);
  private readonly corporatePermissions = inject(PermissionsService);

  /** Whether to offer the user-permissions screen. Mirrors permissionsAdminGuard exactly. */
  get canAdministerPermissions(): boolean {
    return this.corporatePermissions.isPermissionsAdmin();
  }

  // Notifications arrive over SignalR (RealtimeService). This interval is only a safety net for
  // when the hub is unavailable — see startNotificationPolling().
  private readonly FALLBACK_POLLING_INTERVAL = 60000; // 60 seconds
  private isPolling = false;

  public isModuleMenuOpen = false
  public isSettingsOpen = false;
  public isMobileMenuOpen = false;

  // Maps invoicing menu routes to dashboard view/tab when user is on the dashboard
  private readonly dashboardRouteMap: { [route: string]: { view: string; tab: number; subTab?: number } } = {
    'invoicing/invoices-new': { view: 'documents', tab: 1 },
    'invoicing/credit-notes': { view: 'documents', tab: 2 },
    'invoicing/quotations': { view: 'documents', tab: 3 },
    'invoicing/receipts': { view: 'documents', tab: 4 },
    'invoicing/sales': { view: 'documents', tab: 5 },
    'invoicing/vendors': { view: 'vendors', tab: 0 },
    'invoicing/expenses': { view: 'vendors', tab: 1 },
    'invoicing/disbursements-management': { view: 'documents', tab: 7 },
    'invoicing/actual-expenses-management': { view: 'documents', tab: 8 },
    // Settings tabs: 0 Accounting, 1 Accounts, 2 Payment Methods, 3 Number Sequences,
    // 4 Open Banking. VAT rates and the chart of accounts sit inside Accounting, so they
    // carry the nested tab as `subTab` (0 Direct Tax, 1 Indirect Tax, 2 Chart Of Accounts).
    'invoicing/bank-accounts': { view: 'settings', tab: 1 },
    'invoicing/new-payments': { view: 'settings', tab: 2 },
    'invoicing/number-sequences': { view: 'settings', tab: 3 },
    'invoicing/vat-rates': { view: 'settings', tab: 0, subTab: 1 },
    'invoicing/chart-of-accounts': { view: 'settings', tab: 0, subTab: 2 },
  };

  public settings = [
    {
      icon: 'settings',
      name: 'User settings',
      link: '/settings/user'
    },
    {
      // Also reachable from the gear in the notification dropdown, but that is only discoverable
      // once you have opened the dropdown — this is where users look for settings.
      icon: 'notifications',
      name: 'Notification settings',
      link: '/settings/notifications'
    },
    {
      icon: 'corporate_fare',
      name: 'Organization settings',
      link: '/settings/organization'
    }
  ];

  public permissions = [
    ['CorporateUser'],
    ['InvoicingUser'],
    ['ComplianceUser'],
    ['HRUser'],
    ['ReportingUser'],
    ['TaxUser'],
    ['SignaturesUser']
  ];

  public modules$: Observable<IModule[]>;
  // Per-module access, keyed by module id. Recomputed each time the switcher opens (permissions
  // can change on company switch). Drives the grayed-out / non-clickable state for modules the
  // user isn't licensed/permissioned for. `coming-soon` modules ignore this and stay clickable.
  public moduleAccess: Record<number, boolean> = {};
  // Sequence number for computeModuleAccess() — see the guard there.
  private moduleAccessRun = 0;
  public userCompanies: UserCompany[];
  public managedCompanyId: number;
  public filteredCompanies: UserCompany[];
  public isOnboarding = false;
  isAdmin: boolean = false;

  notifications: NotificationVM[] = []
  get unreadCount(): number {

    //console.log('Unread notifications count:', this.notifications.filter(n => !n.read).length);
    return this.notifications.filter(n => !n.isRead).length;
  }

  // Global Search
  searchControl = new FormControl('');
  searchResults: GroupedSearchResults[] = [];
  isSearching = false;
  showSearchResults = false;
  showSearchFilters = false;
  activeSearchFilters: string[] = [];
  // Empty array = search across all companies the user has access to.
  selectedSearchCompanyIds: number[] = [];

  // Search filter options. The values are entity-type filters the module APIs understand
  // (see FILTER_ENTITY_TYPES in global-search.service) — no chip selected searches everything.
  searchFilterOptions = [
    { label: 'Clients', value: 'clients', icon: 'people' },
    { label: 'Invoicing', value: 'invoicing', icon: 'receipt_long' },
    { label: 'Documents', value: 'documents', icon: 'description' },
    { label: 'Notes', value: 'notes', icon: 'sticky_note_2' },
    { label: 'People', value: 'people', icon: 'badge' },
    { label: 'Planner', value: 'tasks', icon: 'task_alt' },
    { label: 'Ref.No', value: 'refno', icon: 'tag' }
  ];

  /**
   * Modules that could not answer the last search. Rendered as a notice above the results so a
   * partial answer is never mistaken for a complete one.
   */
  unavailableModules: UnavailableModule[] = [];

  /**
   * Index of the keyboard-highlighted row within {@link flatResults}, or -1 for none.
   *
   * Flattened across groups on purpose: the arrow keys walk the list the user sees, which runs
   * through the module headings rather than stopping at them.
   */
  activeResultIndex = -1;

  viewPendingLeaves(note: any) {
    // Open the Leave Administration queue (id-free route) and carry the notification's
    // leaveRequestId so the queue can spotlight that specific request on arrival. The queue is
    // company-scoped, so leave from another company is offered as "Open in <organisation>".
    const queryParams = note?.leaveRequestId != null ? { requestId: note.leaveRequestId } : {};
    if (this.isOtherCompany(note?.companyId) && this.userCompanies?.some(c => c.id === note.companyId)) {
      const org = this.companyNameOf(note.companyId);
      offerOtherOrgTab(this.snackBar, this.router, {
        message: `This leave request is in ${org || 'another organisation'}.`,
        orgName: org, path: '/hr/leave/pending', queryParams, companyId: note.companyId,
      });
      return;
    }
    this.router.navigate(['/hr/leave/pending'], { queryParams });
  }

  markAsRead(note: NotificationVM, index: number): void {
    this.formsService.updateNotificationBasedOnID(note.id).subscribe(() => {
      // New array reference, not an in-place flag flip. app-notification-dropdown rebuilds its own
      // enriched copy in ngOnChanges, which Angular only fires when the @Input reference changes —
      // mutating the object inside the array left the row showing as unread until the next refetch.
      this.notifications = this.notifications.map(
        n => n.id === note.id ? { ...n, isRead: true } : n
      );
    });

    const taskList = taskListLink(note);
    if (note.meetingNoteId) {
      this.redirectToMeetingNote(note);
    } else if (taskList) {
      // New tab on this tab's organisation; a client it cannot reach opens view-only.
      openInOrgTab(this.router, taskList.path, taskList.queryParams, null);
    } else if (isPendingChangeNotification(note)) {
      this.openPendingChangeNotification(note);
    } else if (note.globalRequestId) {
      // "Your request was approved/rejected" is a status update for the requester — there is
      // nothing to action, so it still goes to their own list. An approval request opens the
      // dialog, where it can be approved or declined outright.
      const isRequesterUpdate = (note.message || '').toLowerCase().startsWith('your');
      if (isRequesterUpdate) {
        this.router.navigate(['/global-requests/my-requests']);
      } else {
        this.globalRequestDialog.openRequest(note.globalRequestId).subscribe();
      }
    }
  }

  /**
   * Route a pending-change notification to the admin queue. The page is scoped by
   * managedCompanyId, so a notification from another company is offered as "Open in
   * <organisation>" — this tab keeps the company it is on.
   */
  private openPendingChangeNotification(note: NotificationVM): void {
    const target = '/admin/pending-changes';
    const targetCompanyId = note.companyId;

    if (this.isOtherCompany(targetCompanyId)) {
      const org = this.companyNameOf(targetCompanyId);
      offerOtherOrgTab(this.snackBar, this.router, {
        message: `This change is waiting for approval in ${org || 'another organisation'}.`,
        orgName: org, path: target, companyId: targetCompanyId,
      });
      return;
    }

    this.router.navigate([target]);
  }

  /** True when `companyId` is set and is not the company this tab is on. */
  private isOtherCompany(companyId: number | null | undefined): companyId is number {
    return !!companyId && companyId !== this.companiesService.managedCompanyId;
  }

  /**
   * A tab opened with "Open in <organisation>" says once which organisation it is now working in,
   * so nobody edits there thinking they are still in the one they came from. Once per arrival: the
   * flag stays in the URL and a refresh should not repeat it. Keyed on the URL, not a plain flag,
   * because window.open copies sessionStorage into the new tab and would carry "already greeted".
   *
   * Takes the name from the company list, matched to this tab's company, never the stored
   * managedCompanyName: that copied sessionStorage still holds the opener tab's organisation, so
   * a tab opened from ATCA into Bizcombo was greeted with "You're now working in ATCA".
   */
  private greetOrgArrival(companies: { id: number; name: string }[]): void {
    if (!new URLSearchParams(window.location.search).has(ORG_ARRIVAL_PARAM)) return;
    const name = companies.find(c => c.id === this.companiesService.managedCompanyId)?.name?.trim();
    if (!name) return;
    try {
      if (sessionStorage.getItem(ORG_ARRIVAL_PARAM) === window.location.href) return;
      sessionStorage.setItem(ORG_ARRIVAL_PARAM, window.location.href);
    } catch { /* storage blocked: greet anyway */ }

    this.snackBar.open(`You're now working in ${name}.`, 'OK', { duration: 6000 });
  }

  /** The name of one of the user's companies, for "Open in <organisation>". */
  private companyNameOf(companyId: number): string | null {
    return this.userCompanies?.find(c => c.id === companyId)?.name ?? null;
  }

  markAllAsRead(): void {
    this.formsService.readAllNotifications().subscribe(() => {
      // New reference for the same reason as markAsRead: an in-place flag flip never reaches the
      // dropdown, so "mark all read" cleared the badge but left every row looking unread.
      this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));
    })
  }


  public isLoading = false; // Added isLoading property

private snackBar = inject(MatSnackBar); // Properly injected MatSnackBar
private dialog = inject(MatDialog);
private msalService = inject(MsalService);
private readonly redirectHandlerService = inject(RedirectHandlerService);
  deleteNotification(note: NotificationVM, index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Confirm Deletion',text: 'Are you sure you want to delete this notification?',subText: 'Yes'}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.formsService.deleteNotificationBasedOnID(note.id).subscribe({
          next: () => {
            this.formsService.getAllNotifications().subscribe({
              next: (notifications) => {
                this.notifications = notifications.map(n => enrichNotification(n));
                this.isLoading = false;
                this.snackBar.open('Notification deleted!', 'Close', {
                  duration: 3000,
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
              },
              error: (error) => {
                this.isLoading = false;
                this.snackBar.open(`Error refreshing notifications: ${error.message}`, 'Close', {
                  duration: 5000,
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
              }
            });
          },
          error: (error) => {
            this.isLoading = false;
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          }
        });
      }
    });
  }

  approveNotification(note: NotificationVM, index: number, status: number): void {
    let statusText = '';
    switch (status) {
      case 0: statusText = 'N/a'; break;
      case 1: statusText = 'Pending'; break;
      case 2: statusText = 'Approved'; break;
      case 3: statusText = 'Rejected'; break;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Action',
        text: `Are you sure you want to mark this notification as "${statusText}"?`,
        subText: 'Confirm'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.formsService.leaveUpdateBasedOnID(note.timeSheetEntryId, status).subscribe({
          next: () => {
            this.formsService.getAllNotifications().subscribe({
              next: (notifications) => {
                this.notifications = notifications.map(n => enrichNotification(n));
                this.isLoading = false;
                this.snackBar.open(`Notification marked as "${statusText}"!`, 'Close', {
                  duration: 3000,
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
              },
              error: (error) => {
                this.isLoading = false;
                this.snackBar.open(`Error refreshing notifications: ${error.message}`, 'Close', {
                  duration: 5000,
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
              }
            });
          },
          error: (error) => {
            this.isLoading = false;
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          }
        });
      }
    });
  }

  ngOnInit() {
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      this.isOnboarding = event.url.includes('auth/form');
      // A nav dropdown that navigated is done with; anything still open belongs to the page we
      // just left and would otherwise sit on top of the new one.
      this.closeOpenMenus();
      this.closeSearchPanel();
    });

    this.modules$ = this.modulesService.getModules();
    // The switcher's access flags are derived from ngx-permissions, and those land asynchronously:
    // HomeComponent forkJoins every module's permissions and only then calls loadPermissions(),
    // long after this header initialised. Computing once here read an empty permission set, so
    // modules the user *does* hold access to stayed grayed out until something else happened to
    // recompute — the "I have to refresh a few times before my modules appear" bug. permissions$ is
    // BehaviorSubject-backed, so this still computes immediately on init and then again each time
    // the flags change (cache hydrate on load, API response, company switch).
    this.permissionsService.permissions$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => {
      this.computeModuleAccess();
      // Admin-only menu entries (DMS › Taxonomy, Records › Organizations) are decided when the menu
      // is built; the permissions land after the first build, so rebuild once they change.
      if (this.menuModuleId !== null) this.changeMenuModule(this.menuModuleId);
    });
    const urlParams = new URLSearchParams(window.location.search);
    const moduleFromUrl = +(urlParams.get('module') || 0);
    const selectedModule = moduleFromUrl || this.modulesService.getSelectedModule() || 1;

    const applyModule = (moduleId: number) => {
      this.modulesService.selectModule(moduleId);
      this.changeModule.emit(moduleId);
      this.changeMenuModule(moduleId);
    };

    // A tab that has just adopted another organization through `?org=` has no permission flags yet —
    // applyOrgQueryParam cleared them — and probing an empty cache denies every module. That denial
    // used to send the tab to the fallback below, whose URL rewrite then took it to Home, so a note
    // link to another organization landed on the dashboard and worked only when opened again. Take
    // the tab's module at face value until the flags arrive, as HomeComponent does.
    const hasCachedModuleFlags = Object.values(MODULE_FLAG_META)
      .some(meta => !!OrgScopeStore.getItem(meta.storageKey));

    if (!hasCachedModuleFlags) {
      applyModule(selectedModule);
    } else {
      this.modulesService.hasModuleAccess(selectedModule).then(hasAccess => {
        if (hasAccess) {
          applyModule(selectedModule);
        } else {
          this.modulesService.getFirstAccessibleModule().then(fallbackModule => {
            const safeModule = fallbackModule || 1;
            applyModule(safeModule);
            // Only Home carries the module in its URL. `navigate([])` from here resolves against the
            // root, so on any other page this "merge the module into the URL" was a navigation to Home.
            if (window.location.pathname === '/') {
              this.router.navigate([], {
                queryParams: { module: safeModule },
                queryParamsHandling: 'merge',
                replaceUrl: true
              });
            }
          });
        }
      });
    }

    this.redirectHandlerService.waitForRedirect().then(() => {
      // Check if logged in before fetching
      if (this.msalService.instance.getAllAccounts().length > 0) {
        // Ensure module is in the URL so each tab is self-contained (only on home page, after auth)
        if (!moduleFromUrl && window.location.pathname === '/') {
          this.router.navigate([], {
            queryParams: { module: selectedModule },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
        this.userService.getCurrentUser(false).pipe(
          catchError((err) => {
            console.error('Error fetching current user:', err);  // Log instead of throw
            return of(null);  // Return fallback observable to continue
          })
        ).subscribe((user) => {
          if (user) {  // Only set if user exists
            this.currentUser = user;
            if (this.corporatePermissionsService.isCorporateAdmin()) {
              this.isAdmin = true;
            }
          }
        });

        this.companiesService.getManagedCompanyIdObsValue().pipe(
          tap((id) => this.managedCompanyId = id)
        ).subscribe();

        this.userService.getUserCompanies().pipe(
          map(userCompanies => userCompanies.companies),
          catchError(err => {
            console.error('Error fetching user companies:', err);  // Log instead of throw
            return of([]);  // Fallback to empty array
          })
        ).subscribe((companies) => {
          const managedCompany = companies.find(company => company.id === this.managedCompanyId);
          this.managedCompanyChange.emit(managedCompany);
          // The company list is only fetched here, so this is where the active company's name
          // becomes known. The tab title reads it from there rather than fetching it again.
          this.companiesService.setManagedCompanyName(managedCompany?.name);
          this.greetOrgArrival(companies);
          // Sort companies alphabetically by name (ascending)
          const sortedCompanies = [...companies].sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );
          this.userCompanies = sortedCompanies;
          this.filteredCompanies = sortedCompanies;
        });

        // Tie to the component lifecycle: on mobile the shell auto-redirects to /m,
        // which destroys this header. Without takeUntilDestroyed the late HTTP response
        // still runs this callback and calls startNotificationPolling() -> takeUntilDestroyed
        // on an already-destroyed ref, throwing NG0911.
        this.formsService.getAllNotifications().pipe(
          takeUntilDestroyed(this.destroy)
        ).subscribe(notifications => {
          this.notifications = notifications.map(n => enrichNotification(n));
          this.startNotificationPolling();
        });

        // Start notes badge polling. Only where the Notes button exists — the badge sits on it.
        if (canSeeGeneralNotes()) {
          this.meetingNotesPanelService.startBadgePolling();
        }
      } else {
        console.log('No active account - skipping user/company fetches.');
      }
    });

    this.selectedModuleName$ = this.modulesService.moduleName$;

    // Initialize global search
    this.initializeSearch();
  }

  private initializeSearch(): void {
    this.globalSearchService.unavailableModules$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(modules => this.unavailableModules = modules);

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(value => {
        this.isSearching = value && value.length >= 2;
        if (!value || value.length < 2) {
          this.searchResults = [];
          this.showSearchResults = false;
        }
      }),
      filter(value => !!value && value.length >= 2),
      switchMap(value => {
        return this.globalSearchService.searchProgressive(value!, this.activeSearchFilters, this.selectedSearchCompanyIds).pipe(
          catchError(() => of({ groups: [] as GroupedSearchResults[], pending: 0 }))
        );
      }),
      takeUntilDestroyed(this.destroy)
    ).subscribe(({ groups, pending }) => this.applyResults(groups, pending));
  }

  /**
   * Results arrive already filtered: the chips are sent to the modules as entity-type filters, so
   * each one only returns what was asked for. The old client-side pass over category names went
   * with the flat category list — groups are modules now, and a chip no longer maps to one group
   * ("Invoicing" holds both vendors and invoices, which come from two different services).
   */
  private applyResults(results: GroupedSearchResults[], pending = 0): void {
    // Results arrive module by module; keep the highlight on the same row while later modules
    // fill in around it, and only reset it on a fresh search.
    const highlighted = this.activeResultIndex >= 0 ? this.flatResults[this.activeResultIndex] : null;
    this.searchResults = results;
    this.activeResultIndex = highlighted ? this.flatResults.findIndex(r => r.id === highlighted.id && r.type === highlighted.type) : -1;
    this.isSearching = pending > 0;
    this.showSearchResults = this.searchResults.length > 0 || this.unavailableModules.length > 0;
  }

  onSearchResultClick(result: GlobalSearchResult): void {
    // Nothing to open: the user has no access to the company that holds it. Say so, and offer
    // the share request right there — this is also what Enter on the row lands on.
    if (result.restricted) {
      this.snackBar
        .open(`You don't have access to ${result.title}${result.companyName ? ' in ' + result.companyName : ''}.`,
          'Request share', { duration: 8000 })
        .onAction()
        .subscribe(() => this.requestShareFromSearch(result));
      return;
    }

    this.showSearchResults = false;
    this.showSearchFilters = false;
    this.searchControl.setValue('');

    // localStorage is shared by every tab, so this hand-off also reaches a new tab opened below.
    if (result.clientData) {
      localStorage.setItem('invoicingClient', JSON.stringify(result.clientData));
    }

    // A click never moves this tab to another organisation. A client from another one opens here
    // (view-only with Request share when this organisation cannot reach it); anything else only
    // exists over there, so it is offered as "Open in <organisation>" rather than jumped to.
    if (this.isOtherCompany(result.companyId) && result.type !== 'client') {
      offerOtherOrgTab(this.snackBar, this.router, {
        message: `${result.title} is in ${result.companyName || 'another organisation'}.`,
        orgName: result.companyName,
        path: result.route,
        queryParams: result.queryParams,
        companyId: result.companyId,
      });
      return;
    }

    if (result.queryParams) {
      this.router.navigate([result.route], { queryParams: result.queryParams });
    } else {
      this.router.navigate([result.route]);
    }
  }

  /** Client rows asked for from the search box this session (`id`), so a request is sent once. */
  shareRequestedFormIds = new Set<string>();

  /**
   * A client this tab's company cannot reach: one the user can open only in another of their
   * companies, or one in a company they have no access to at all. Either way the owner can share
   * it into this company, through the Global requests inbox.
   */
  canRequestShare(result: GlobalSearchResult): boolean {
    if (result.type !== 'client' || !result.ownerCompanyId) return false;
    // The dedupe keeps the current company's row whenever the client is reachable from here, so
    // a row from another company means this one cannot see it.
    return !!result.restricted || this.isOtherCompany(result.companyId);
  }

  requestShareFromSearch(result: GlobalSearchResult, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const formId = Number(result.id);
    if (!formId || !result.ownerCompanyId || this.shareRequestedFormIds.has(String(result.id))) return;

    this.shareRequestedFormIds.add(String(result.id));
    this.formsService.requestClientShare(formId, result.ownerCompanyId, result.title, {
      // companyName is the owner's only when the row is the owner's; a shared-in copy is named
      // after the company it was shared into.
      ownerCompanyName: result.companyId === result.ownerCompanyId ? result.companyName : undefined,
      originCompanyName: this.currentCompanyName || undefined,
    }).subscribe({
      next: () => this.snackBar.open(`Share request sent for "${result.title}".`, 'OK', { duration: 5000 }),
      error: (err) => {
        this.shareRequestedFormIds.delete(String(result.id));
        this.snackBar.open(err?.error?.errors || 'Could not send the share request.', 'OK', { duration: 5000 });
      },
    });
  }

  /**
   * Admin-only entry point: open another company's organization settings in a new tab
   * WITHOUT changing this tab's managed company. The new tab is scoped to the target
   * company via `?org=<companyId>` — each tab keeps its own active company + permissions
   * in the per-tab OrgScopeStore, so this originating tab is untouched (no localStorage
   * lock/polling needed anymore).
   *
   * The settings page expects ?submittedFormId=<id> alongside the company id — that's the
   * company's internal-company SubmittedForm. We look it up first.
   */
  /**
   * Open a company's own record in Organization Settings, in this tab.
   *
   * The gear on each row opens the same page in a new tab, which is what you want when you are
   * comparing companies. Picking the company itself is the other intent — go there — and it had
   * no handler, so clicking the name did nothing at all.
   *
   * Gated on isAdmin because /settings/company/:id carries pagePermissionGuard: without the
   * check a non-admin's click routes them to '/' with no explanation, which reads as the picker
   * being broken rather than as a permission boundary.
   */
  openCompanySettingsInPlace(companyId: number, event: MouseEvent, trigger?: MatMenuTrigger): void {
    event.stopPropagation();
    event.preventDefault();
    if (!companyId || !this.isAdmin) return;

    this.formsService.getInternalCompanyFormId(companyId).pipe(
      catchError(() => of(null))
    ).subscribe(formId => {
      if (!formId) {
        this.snackBar.open('No internal client form found for this company.', 'Close', { duration: 3000 });
        return;
      }

      // Closed explicitly. Navigating swaps the routed component underneath, but the header —
      // and this overlay with it — survives, so the menu would otherwise sit open on top of the
      // page it just opened.
      trigger?.closeMenu();
      // Another company's settings never take over this tab: it stays on its organisation, and
      // the settings open in a new tab that says which organisation it is in.
      if (this.isOtherCompany(companyId)) {
        openInOtherOrgTab(this.router, ['/settings/company', companyId], { submittedFormId: formId }, companyId);
        return;
      }
      this.router.navigate(['/settings/company', companyId], {
        queryParams: { submittedFormId: formId, org: companyId }
      });
    });
  }

  openCompanySettings(companyId: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!companyId) return;

    this.formsService.getInternalCompanyFormId(companyId).pipe(
      catchError(() => of(null))
    ).subscribe(formId => {
      if (!formId) {
        this.snackBar.open('No internal client form found for this company.', 'Close', { duration: 3000 });
        return;
      }

      openInOrgTab(this.router, ['/settings/company', companyId], { submittedFormId: formId }, companyId);
    });
  }

  /**
   * Open another organization company's "as a client" form in a new tab without
   * changing the currently-managed company on this tab. The /edit-form/:id route's
   * managedCompanySwitchGuard looks up the form's owning company server-side and
   * switches the new tab's managed company in-memory — no URL param needed.
   */
  openCompanyAsForm(companyId: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!companyId || companyId === this.managedCompanyId) return;

    this.formsService.getInternalCompanyFormId(companyId).pipe(
      catchError(() => of(null))
    ).subscribe(formId => {
      if (!formId) {
        this.snackBar.open('No client form found for this company.', 'Close', { duration: 3000 });
        return;
      }
      const urlTree = this.router.createUrlTree(['/edit-form', formId]);
      window.open(this.router.serializeUrl(urlTree), '_blank');
    });
  }

  /**
   * Authoritative "what's this tab's company" reading. The dropdown trigger and the
   * row highlight bind to this rather than `this.managedCompanyId` so they reflect the
   * company persisted for THIS tab (per-tab OrgScopeStore), even when a sibling action
   * has temporarily flipped the in-memory BehaviorSubject.
   */
  get currentCompanyIdFromStorage(): number {
    const stored = OrgScopeStore.getItem('managedCompanyId');
    return stored ? +stored : 0;
  }

  /** Display label for the company-picker trigger. */
  get currentCompanyName(): string {
    if (!this.userCompanies?.length || !this.currentCompanyIdFromStorage) return '';
    return this.userCompanies.find(c => c.id === this.currentCompanyIdFromStorage)?.name || '';
  }

  /** Reset the filter when the picker menu opens. */
  onCompanyMenuOpened(): void {
    this.filteredCompanies = this.userCompanies || [];
  }

  onSearchFocus(): void {
    this.showSearchFilters = true;
    if (this.searchResults.length > 0) {
      this.showSearchResults = true;
    }
  }

  /**
   * True while the company-scope menu is open.
   *
   * mat-menu renders in a CDK overlay with a focus trap, so opening it blurs the search input even
   * though the trigger suppresses the mousedown. Without this the panel showed the menu for an
   * instant and then closed itself underneath it.
   */
  searchCompaniesMenuOpen = false;

  onSearchCompaniesMenuClosed(): void {
    this.searchCompaniesMenuOpen = false;

    // Put the caret back so the next blur is a real one — the user is still mid-search, and the
    // panel should close when they leave it, not when they finish choosing companies.
    //
    // Deferred, and only while the panel is still open: the click that dismissed the menu lands on
    // its backdrop and reaches the document handler after this runs. If that click was outside the
    // search, the panel is already gone by then and refocusing would pull it straight back open.
    setTimeout(() => {
      if (this.showSearchFilters || this.showSearchResults) {
        this.searchInput?.nativeElement.focus();
      }
    });
  }

  onSearchBlur(): void {
    // Delay to allow click events on results and filters
    setTimeout(() => {
      // Focus moved into the company menu, not away from the search. Closing here would dismiss the
      // panel the menu belongs to.
      if (this.searchCompaniesMenuOpen) return;

      // Closes whether or not filters are active: the chips are restored on the next focus, and the
      // placeholder ("Search in Clients...") plus the clear button keep the state visible meanwhile.
      // Keeping the panel open for them left it stranded over the page once a filter was picked.
      this.closeSearchPanel();
    }, 200);
  }

  private closeSearchPanel(): void {
    this.showSearchResults = false;
    this.showSearchFilters = false;
    this.activeResultIndex = -1;
  }

  /**
   * The panel is a plain div rather than an overlay, so nothing dismisses it for us: the filter
   * chips suppress the mousedown to keep the caret in the input, and the company-scope menu hands
   * focus back when it closes.
   */
  private isClickInsideSearch(event: MouseEvent): boolean {
    const container = this.searchContainer?.nativeElement;
    if (!container) return false;

    const target = event.target as HTMLElement | null;
    if (target && container.contains(target)) return true;
    // The company-scope menu renders in its own overlay, outside the container, but belongs to it.
    if (target?.closest?.('.search-companies-menu')) return true;

    // While that menu is open its backdrop swallows the click, so the target is the backdrop no
    // matter where the pointer was — ask what is underneath it instead.
    if (event.clientX || event.clientY) {
      return document.elementsFromPoint(event.clientX, event.clientY)
        .some(el => container.contains(el) || !!el.closest('.search-companies-menu'));
    }
    return false;
  }

  /** Shuts every header menu that is currently open. Closing a parent closes its submenus. */
  private closeOpenMenus(): void {
    this.menuTriggers?.forEach(trigger => {
      if (trigger.menuOpen) {
        trigger.closeMenu();
      }
    });
  }

  toggleSearchFilter(filterValue: string): void {
    const index = this.activeSearchFilters.indexOf(filterValue);
    if (index > -1) {
      this.activeSearchFilters.splice(index, 1);
    } else {
      this.activeSearchFilters.push(filterValue);
    }

    this.runSearch();
  }

  isFilterActive(filterValue: string): boolean {
    return this.activeSearchFilters.includes(filterValue);
  }

  toggleCompanyFilter(companyId: number): void {
    const idx = this.selectedSearchCompanyIds.indexOf(companyId);
    if (idx > -1) {
      this.selectedSearchCompanyIds.splice(idx, 1);
    } else {
      this.selectedSearchCompanyIds.push(companyId);
    }
    this.runSearch();
  }

  isCompanyFilterActive(companyId: number): boolean {
    return this.selectedSearchCompanyIds.includes(companyId);
  }

  selectAllSearchCompanies(): void {
    this.selectedSearchCompanyIds = [];
    this.runSearch();
  }

  get searchCompanyButtonLabel(): string {
    if (this.selectedSearchCompanyIds.length === 0) {
      return 'All companies';
    }
    if (this.selectedSearchCompanyIds.length === 1) {
      const company = this.userCompanies?.find(c => c.id === this.selectedSearchCompanyIds[0]);
      return company?.name || '1 company';
    }
    return `${this.selectedSearchCompanyIds.length} companies`;
  }

  protected runSearch(): void {
    if (!this.searchControl.value || this.searchControl.value.length < 2) {
      return;
    }
    this.isSearching = true;
    this.globalSearchService.searchProgressive(
      this.searchControl.value,
      this.activeSearchFilters,
      this.selectedSearchCompanyIds
    ).pipe(
      catchError(() => of({ groups: [] as GroupedSearchResults[], pending: 0 }))
    ).subscribe(({ groups, pending }) => this.applyResults(groups, pending));
  }

  /**
   * Every visible row in display order, so the arrow keys can walk the whole list rather than one
   * module at a time.
   */
  get flatResults(): GlobalSearchResult[] {
    return this.searchResults.flatMap(group => group.results);
  }

  /** True for the row the arrow keys are currently on — drives the highlight and aria-activedescendant. */
  isResultActive(result: GlobalSearchResult): boolean {
    return this.activeResultIndex >= 0 && this.flatResults[this.activeResultIndex] === result;
  }

  /**
   * Arrow keys move the highlight, Enter opens it. Wraps at both ends, which is what a type-ahead
   * list is expected to do: the fastest way to the last result is Up from the first.
   *
   * Nothing is highlighted until the user presses a key, so Enter with no selection stays free for
   * whatever a full-results view is bound to later — it does not silently open the top hit.
   */
  onSearchKeydown(event: KeyboardEvent): void {
    const results = this.flatResults;

    if (event.key === 'ArrowDown') {
      if (!results.length) return;
      event.preventDefault();
      this.activeResultIndex = (this.activeResultIndex + 1) % results.length;
      this.scrollActiveResultIntoView();
      return;
    }

    if (event.key === 'ArrowUp') {
      if (!results.length) return;
      event.preventDefault();
      this.activeResultIndex = this.activeResultIndex <= 0
        ? results.length - 1
        : this.activeResultIndex - 1;
      this.scrollActiveResultIntoView();
      return;
    }

    if (event.key === 'Enter' && this.activeResultIndex >= 0) {
      const result = results[this.activeResultIndex];
      if (result) {
        event.preventDefault();
        this.onSearchResultClick(result);
      }
    }
  }

  /**
   * Keeps the highlighted row visible in the scrolling dropdown. The rows are plain divs with no
   * focus of their own — focus stays in the input so typing keeps working — so the browser does
   * not scroll to them by itself.
   */
  private scrollActiveResultIntoView(): void {
    const container = this.searchContainer?.nativeElement;
    const row = container?.querySelector(`[data-result-index="${this.activeResultIndex}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }

  getSearchPlaceholder(): string {
    if (this.activeSearchFilters.length === 0) {
      return 'Search clients, invoices, documents...';
    }
    const labels = this.activeSearchFilters.map(f =>
      this.searchFilterOptions.find(opt => opt.value === f)?.label || f
    );
    return `Search in ${labels.join(', ')}...`;
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.searchResults = [];
    this.showSearchResults = false;
    this.showSearchFilters = false;
    this.activeSearchFilters = [];
    this.selectedSearchCompanyIds = [];
  }

  navigateToNotifications(): void {
    this.router.navigate(['/notifications']);
  }

  navigateToNotificationSettings(): void {
    this.router.navigate(['/settings/notifications']);
  }

  toggleModuleMenu() {
    this.isModuleMenuOpen = !this.isModuleMenuOpen;
    // Refresh access flags on open — the user may have switched company since the last render.
    if (this.isModuleMenuOpen) this.computeModuleAccess();
  }

  /** Resolve permission + license access for every module so the switcher can gray out the ones
   *  the user can't enter. `coming-soon` modules are excluded from gating (always clickable). */
  private async computeModuleAccess(): Promise<void> {
    // Runs on every permission change, so passes can overlap and finish out of order. Only the
    // newest one may publish, or a stale pass started against an older (usually emptier)
    // permission set can overwrite the result of the newer one and re-gray the tiles.
    const run = ++this.moduleAccessRun;
    const modules = await firstValueFrom(this.modulesService.getModules());
    const results = await Promise.all(
      modules.map(async m => [m.id, await this.modulesService.hasModuleAccess(m.id)] as [number, boolean])
    );
    if (run !== this.moduleAccessRun) return;
    this.moduleAccess = Object.fromEntries(results);
  }

  /** A built module the user can enter. */
  hasModuleAccess(module: IModule): boolean {
    return !!this.moduleAccess[module.id];
  }

  isComingSoon(module: IModule): boolean {
    return module.status === 'coming-soon';
  }

  /** External modules (e.g. Billing) live in a separate app and open a URL instead of a route. */
  isExternal(module: IModule): boolean {
    return !!module.externalUrl;
  }

  /** Shown but grayed / non-clickable: a module the user isn't permissioned/licensed for. Applies
   *  to in-app AND external modules (e.g. Billing). Coming-soon tiles are always clickable. */
  isModuleDisabled(module: IModule): boolean {
    return !this.isComingSoon(module) && !this.hasModuleAccess(module);
  }

  /** Switcher tile click. No access → ignored; external → open its URL; coming-soon → Coming Soon
   *  page; else enter the in-app workspace. */
  onModuleClick(module: IModule): void {
    if (this.isModuleDisabled(module)) return;
    if (this.isExternal(module)) {
      this.isModuleMenuOpen = false;
      window.open(module.externalUrl, '_blank', 'noopener');
      return;
    }
    if (this.isComingSoon(module)) {
      this.isModuleMenuOpen = false;
      this.router.navigate(['/coming-soon'], { queryParams: { module: module.name } });
      return;
    }
    this.selectModule(module.id);
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  navigateToSettings() {
    this.isSettingsOpen = !this.isSettingsOpen;
  }

  openModuleInNewTab(id: number) {
    this.isModuleMenuOpen = false;
    // Seed the new tab with this tab's organization: organizations are per tab, and a new tab
    // without ?org= falls back to the first organization opened in the browser.
    const org = this.companiesService.managedCompanyId;
    const urlTree = this.router.createUrlTree([''], { queryParams: org ? { module: id, org } : { module: id } });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  /**
   * Switches this tab's module in place.
   *
   * It used to navigate and then reload the whole app, which re-ran the tenant bootstrap, every
   * permission call and the full bundle check on each switch. None of that changes with the module:
   * permissions for every module are already loaded, and each module either owns its own route
   * (a fresh component) or is a Home dashboard, which now follows `?module=` itself
   * (HomeComponent). What does depend on the module is stored here — the tab's module, and the
   * read-only (Viewer) flags that are evaluated against it.
   */
  selectModule(id: number) {
    this.isModuleMenuOpen = false;
    this.modulesService.selectModule(id);
    this.readOnlyService.invalidate();
    this.changeModule.emit(id);
    this.changeMenuModule(id);

    const landing = this.moduleLandingRoute(id);
    this.router.navigate(landing ?? [''], { queryParams: { module: id } });
  }

  /**
   * selectModule's bookkeeping without its navigation, for callers that navigate themselves (the
   * One Workspace sidebar opens a module's page directly rather than its landing page).
   */
  applyModuleInPlace(id: number): void {
    this.isModuleMenuOpen = false;
    this.modulesService.selectModule(id);
    this.readOnlyService.invalidate();
    this.changeModule.emit(id);
    this.changeMenuModule(id);
  }
  private readonly readOnlyService = inject(ReadOnlyService);

  /** Modules that open a dedicated page instead of the generic home dashboard. Returns the
   *  router commands, or null to fall back to home ('/'). */
  protected moduleLandingRoute(id: number): any[] | null {
    // HR: super admins land on the employee-list dashboard; everyone else on their own profile.
    if (id === 4) {
      if (this.isHrAdmin()) return ['/hr'];
      return this.currentUser?.userId ? ['/employee', this.currentUser.userId, 'profile-details'] : null;
    }
    // Reporting opens the reports page directly.
    if (id === 5) return ['/reports'];
    // Tax opens its dashboard.
    if (id === 6) return ['/tax'];
    // Accounting opens the ported module's shell, which redirects to its own dashboard.
    if (id === 7) return ['/accounting'];
    // Inventory likewise owns a workspace with its own sidebar; its routes redirect to products.
    if (id === 13) return ['/inventory'];
    // Migration owns a workspace too; its routes redirect to the casework overview.
    if (id === 11) return ['/migration'];
    // Audit's shell redirects to its own dashboard. Only reachable once the tile leaves
    // 'coming-soon' — see the id === 9 menu below.
    if (id === 9) return ['/audit'];
    // Custodis's shell redirects to its own default vault list (Logins).
    if (id === 15) return ['/custodis'];
    // Signatures opens the user's own signature rather than an admin screen: everyone in the group
    // has one, only a handful administer templates.
    if (id === 14) return ['/signatures/all'];
    return null;
  }

  /**
   * Records dropdown = the same category list as the forms-table side menu. Each item carries a
   * `category` query param so clicking it lands on forms-table with that side-menu item selected.
   * Creatable types (Individuals / Business / Referrals) expose View + Create sub-items, and
   * Organizations is admin-only — both mirroring the side menu exactly.
   */
  private recordsMenuChildren(): any[] {
    const cat = (name: string, key: string, icon: string): any => ({
      name, route: 'forms-table', icon, queryParam: null, queryParams: { category: key }, children: null
    });
    const view = (key: string): any => ({
      name: 'View', route: 'forms-table', icon: 'visibility', queryParam: null, queryParams: { category: key }, children: null
    });
    const create = (formType: number, name = 'Create', queryParams: Record<string, any> | null = null): any => ({
      name, route: `create-form/${formType}`, icon: 'add', queryParam: null, queryParams, children: null
    });

    const children: any[] = [
      cat('Clients', 'clients', 'groups'),
      { name: 'Individuals', route: 'forms-table', icon: 'person',   queryParam: null, children: [view('individuals'), create(FormTypeEnum.Individual)] },
      { name: 'Business',    route: 'forms-table', icon: 'business', queryParam: null, children: [view('business'),    create(FormTypeEnum.Business)] },
      { name: 'Referrals',   route: 'forms-table', icon: 'handshake', queryParam: null, children: [
        view('referrals'),
        // Both create an Introducer form; the kind picks the IB / AG code prefix.
        create(FormTypeEnum.Introducer, 'Create Introducer', { kind: referralKindToParam(ReferralKind.Introducer) }),
        create(FormTypeEnum.Introducer, 'Create Agent', { kind: referralKindToParam(ReferralKind.Agent) }),
      ] },
      cat('Potential Clients', 'potential', 'person_search'),
      // Its own page, not a forms-table category. The shell shows its "Needs action" count.
      { name: 'New from the portal', route: 'portal-applications', icon: 'move_to_inbox', queryParam: null,
        queryParams: null, children: null, badge: 'portalNeedsAction' },
      cat('New Cyco Registrations', 'cyco', 'app_registration'),
      // Create opens the New Business wizard on its "Shelf name" intake (forms-table reads ?create=).
      { name: 'Shelf Names', route: 'forms-table', icon: 'inventory_2', queryParam: null, children: [
        view('shelf'),
        { name: 'Create', route: 'forms-table', icon: 'add', queryParam: null, queryParams: { category: 'shelf', create: 1 }, children: null },
      ] },
      cat('Lost Contact', 'lost-contact', 'phone_disabled'),
      cat('Nominees', 'nominees', 'badge'),
      cat('Officers', 'officers', 'gavel'),
      cat('UBOs', 'ubos', 'diversity_3'),
      cat('Shareholders', 'shareholders', 'pie_chart'),
      cat('Employees', 'employees', 'work'),
      cat('Family Members', 'family', 'family_restroom'),
    ];

    if (this.corporatePermissionsService.isCorporateAdmin()) {
      children.push(cat('Organizations', 'organizations', 'corporate_fare'));
    }

    return children;
  }

  private isHrAdmin(): boolean {
    try {
      const raw = OrgScopeStore.getItem('hrPermissions');
      return !!raw && (JSON.parse(raw) as string[]).includes('HRAdmin');
    } catch {
      return false;
    }
  }

  hideAfterClick() {
    this.isSettingsOpen = false;
  }

  /** The module whose menu was last built (null until the first build). */
  private menuModuleId: number | null = null;

  private changeMenuModule(id: number) {
    this.menuModuleId = id;
    if (id === 1) {
      // Each area's sub-pages live in the sidebar (Option D mockups); the pages read ?tab=.
      const tab = (name: string, route: string, key: string) => ({ name, route, queryParams: { tab: key }, children: null });
      const menu = [
        {
          icon: 'home',
          name: 'Dashboard',
          // The dashboard is the home route ('/'): HomeComponent renders it for this module.
          route: '',
          children: [
            tab('Overview', '', 'overview'),
            tab('My Clients', '', 'clients'),
            tab('Important dates', '', 'important-dates'),
            tab('Service due dates', '', 'service-due-dates'),
            tab('Services search', '', 'services-search'),
            tab('My tasks', '', 'my-tasks'),
            tab('Recent documents', '', 'recent-documents'),
          ]
        },
        {
          name: 'Time sheet',
          icon: 'schedule',
          route: 'time-sheet'
        },
        {
          icon: 'storage',
          name: 'Records',
          route: 'forms-table',
          children: this.recordsMenuChildren()
        },
        {
          name: 'DMS',
          icon: 'description',
          route: 'file-system',
          children: this.corporatePermissionsService.isCorporateAdmin()
            ? [tab('Documents', 'file-system', 'documents'), tab('Taxonomy', 'file-system', 'taxonomy')]
            : null
        },
        {
          name: 'Planner',
          icon: 'checklist',
          route: 'task-planner',
          children: [
            tab('Planner', 'task-planner', 'planner'),
            tab('To-dos', 'task-planner', 'todos'),
            tab('Note tags', 'task-planner', 'tags'),
            // The organisation's own planners, kept off the working list. Everyone sees them;
            // only admins can change them (view-only for the rest, in the planner page).
            tab('Organisation planners', 'task-planner', 'org'),
          ]
        },
        {
          name: 'Notes',
          icon: 'sticky_note_2',
          route: 'tagged-notes',
          notesOnly: true,
          children: null
        },
        {
          name: 'Reference numbers',
          icon: 'tag',
          route: 'reference-numbers',
          children: null
        }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 2) {
      const menu = [
        {
          icon: 'dashboard',
          name: 'Dashboard',
          route: 'invoicing-dashboard'
        },
        {
          name: 'Documents',
          icon: 'description',
          route: 'invoicing/invoices-new',
          children: [
            {
              name: 'Invoices',
              route: 'invoicing/invoices-new',
              icon: 'receipt',
              queryParam: null,
              children: null
            },
            {
              name: 'Credit Notes',
              route: 'invoicing/credit-notes',
              icon: 'note',
              queryParam: null,
              children: null
            },
            {
              name: 'Quotations',
              route: 'invoicing/quotations',
              icon: 'request_quote',
              queryParam: null,
              children: null
            },
            {
              name: 'Receipts',
              route: 'invoicing/receipts',
              icon: 'receipt_long',
              queryParam: null,
              children: null
            },
            {
              name: 'Order/Proposal Form',
              route: 'invoicing/sales',
              icon: 'assignment',
              queryParam: null,
              children: null
            }
          ]
        },
        {
          name: 'Vendors & Expenses',
          icon: 'store',
          route: 'invoicing/vendors',
          children: [
            {
              name: 'Vendors',
              route: 'invoicing/vendors',
              icon: 'store',
              queryParam: null,
              children: null
            },
            {
              name: 'Expenses',
              route: 'invoicing/expenses',
              icon: 'payments',
              queryParam: null,
              children: null
            },
            {
              name: 'Disbursements',
              route: 'invoicing/disbursements-management',
              icon: 'account_balance',
              queryParam: null,
              children: null
            },
            {
              name: 'Actual Expenses',
              route: 'invoicing/actual-expenses-management',
              icon: 'price_check',
              queryParam: null,
              children: null
            }
          ]
        },
        {
          name: 'Settings',
          icon: 'settings',
          route: 'invoicing/new-payments',
          children: [
            {
              name: 'Payment Methods',
              route: 'invoicing/new-payments',
              icon: 'credit_card',
              queryParam: null,
              children: null
            },
            {
              name: 'Bank Accounts',
              route: 'invoicing/bank-accounts',
              icon: 'account_balance',
              queryParam: null,
              children: null
            },
            {
              name: 'VAT Rates',
              route: 'invoicing/vat-rates',
              icon: 'percent',
              queryParam: null,
              children: null
            },
            {
              name: 'Number Sequences',
              route: 'invoicing/number-sequences',
              icon: 'format_list_numbered',
              queryParam: null,
              children: null
            },
            {
              name: 'Chart Of Accounts',
              route: 'invoicing/chart-of-accounts',
              icon: 'account_tree',
              queryParam: null,
              children: null
            }
          ]
        }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 3) {
      // Compliance workspace. Standalone pages are the risk-assessment settings; per-form
      // compliance is reached from each client form.
      const menu = [
        {
          icon: 'verified_user',
          name: 'Risk Settings',
          route: 'corporate/risk-settings'
        }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 4) {
      // HR module — company-level navigation. Per-employee data lives under /employee/:id
      // (reached from the Employees directory); the admin areas below are single pages that
      // span all employees.
      const menu = [
        { icon: 'groups', name: 'Employees', route: 'hr' },
        { icon: 'euro', name: 'Salary Details', route: 'hr/salary-details' },
        {
          icon: 'admin_panel_settings',
          name: 'Leave Administration',
          route: 'hr/leave',
          children: [
            { name: 'Pending Approval', route: 'hr/leave/pending', icon: 'pending_actions', queryParam: null, children: null },
            { name: 'All Employee Leaves', route: 'hr/leave/all', icon: 'list_alt', queryParam: null, children: null },
            { name: 'Leaves Overview', route: 'hr/leave/overview', icon: 'view_list', queryParam: null, children: null },
            { name: 'Team Calendar', route: 'hr/leave/calendar', icon: 'calendar_month', queryParam: null, children: null },
            { name: 'Mandatory Leaves', route: 'hr/leave/holidays', icon: 'event_busy', queryParam: null, children: null }
          ]
        },
        {
          icon: 'business',
          name: 'Organization',
          route: 'hr/organization',
          children: [
            { name: 'Positions', route: 'hr/organization/positions', icon: 'work', queryParam: null, children: null },
            { name: 'Assignments', route: 'hr/organization/assignments', icon: 'assignment', queryParam: null, children: null },
            { name: 'Salaries', route: 'hr/organization/salaries', icon: 'euro', queryParam: null, children: null },
            { name: 'Departments', route: 'hr/organization/departments', icon: 'account_tree', queryParam: null, children: null },
            { name: 'Organization Chart', route: 'hr/organization/org-chart', icon: 'schema', queryParam: null, children: null }
          ]
        }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 5) {
      // Reporting workspace — re-homes the existing report pages under a dedicated module.
      const menu = [
        {
          icon: 'assessment',
          name: 'Reports',
          route: 'reports'
        },
        {
          icon: 'trending_up',
          name: 'Profitability Report',
          route: 'profitability-report'
        }
      ];
      // Timesheet Reports is admin-only.
      if (this.corporatePermissionsService.isCorporateAdmin()) {
        menu.push({
          icon: 'schedule',
          name: 'Timesheet Reports',
          route: 'time-sheet-reports'
        });
      }
      this.changeMenu.emit(menu);
    } else if (id === 6) {
      // Tax workspace — the ported BC_Tax_Mod filings (VAT / VIES / TD5), the returns list, plus a
      // settings section (rates / periods) behind a sub-shell.
      const menu = [
        { icon: 'account_balance', name: 'Tax Dashboard', route: 'tax/dashboard' },
        { icon: 'receipt_long', name: 'VAT Return', route: 'tax/vat-return' },
        { icon: 'public', name: 'VIES', route: 'tax/vies' },
        { icon: 'calculate', name: 'Temporary Tax — TD5', route: 'tax/temporary-tax' },
        { icon: 'description', name: 'Tax Returns', route: 'tax/returns' },
        {
          icon: 'settings',
          name: 'Settings',
          route: 'tax/settings',
          children: [
            { name: 'Tax Rates', route: 'tax/settings/rates', icon: 'percent', queryParam: null, children: null },
            { name: 'Periods', route: 'tax/settings/periods', icon: 'event', queryParam: null, children: null }
          ]
        }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 7) {
      // Accounting workspace. The module renders its own left sidenav with the full section list,
      // so this stays at top-level destinations only — enough that the header nav isn't empty when
      // Accounting is active, without mirroring the whole sidenav in two places.
      const menu = [
        { icon: 'dashboard', name: 'Dashboard', route: 'accounting/dashboard' },
        { icon: 'edit_note', name: 'Journals', route: 'accounting/journal' },
        { icon: 'balance', name: 'Trial Balance', route: 'accounting/trial-balance' },
        { icon: 'bar_chart', name: 'Financials', route: 'accounting/financials' },
        { icon: 'account_balance', name: 'Banking', route: 'accounting/bank/overview' },
        { icon: 'receipt_long', name: 'Invoices', route: 'accounting/invoices/upload' }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 13) {
      // Inventory workspace. Like Accounting, the module renders its own sidebar with the full
      // group list, so this stays at top-level destinations rather than mirroring it.
      const menu = [
        { icon: 'inventory_2', name: 'Products', route: 'inventory/products' },
        { icon: 'warehouse', name: 'Warehouses', route: 'inventory/warehouses' },
        { icon: 'inventory', name: 'Stock', route: 'inventory/current-stock' },
        { icon: 'point_of_sale', name: 'Sales', route: 'inventory/sales' },
        { icon: 'local_shipping', name: 'Suppliers', route: 'inventory/suppliers' },
        { icon: 'assessment', name: 'Low Stocks', route: 'inventory/low-stocks' }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 11) {
      // Migration workspace. Like Accounting and Inventory, the module renders its own sidebar with
      // the full section list, so this stays at top-level destinations rather than mirroring it.
      const menu = [
        { icon: 'grid_view', name: 'Overview', route: 'migration/dashboard' },
        { icon: 'folder_open', name: 'Cases', route: 'migration/cases' },
        { icon: 'workspaces', name: 'Services', route: 'migration/permits' },
        { icon: 'school', name: 'Institutions', route: 'migration/institutions' },
        { icon: 'auto_awesome', name: 'Recommender', route: 'migration/recommender' },
        { icon: 'bar_chart', name: 'Reports', route: 'migration/reports/pipeline' }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 9) {
      // Audit workspace (ported from BC_Audit_UI). Like Accounting and Migration, the module
      // renders its own sidebar — all 20 screens grouped by audit phase — so this stays at the
      // phase entry points rather than mirroring it.
      const menu = [
        { icon: 'dashboard', name: 'Dashboard', route: 'audit/dashboard' },
        { icon: 'how_to_reg', name: 'Acceptance', route: 'audit/acceptance' },
        { icon: 'task_alt', name: 'Planning', route: 'audit/planning' },
        { icon: 'calculate', name: 'Materiality', route: 'audit/materiality' },
        { icon: 'warning', name: 'Risk Matrix', route: 'audit/risk' },
        { icon: 'description', name: 'Working Papers', route: 'audit/workingpapers' },
        { icon: 'verified', name: 'Audit Opinion', route: 'audit/opinion' }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 14) {
      // Signatures workspace. The module renders its own sidebar, so this stays at the shipped
      // entry points. My Mark, Groups, Deployments and the compliance dashboard are the next drop —
      // add them here as they land rather than listing routes that do not exist yet.
      const menu = [
        { icon: 'view_list', name: 'My Signatures', route: 'signatures/all' },
        { icon: 'forward_to_inbox', name: 'Company Signatures', route: 'signatures/company' },
        { icon: 'domain', name: 'Company Details', route: 'signatures/admin/companies' },
        { icon: 'article', name: 'Templates', route: 'signatures/admin/templates' },
      ];
      this.changeMenu.emit(menu);
    } else if (id === 15) {
      // Custodis renders its own vault sidebar inside its shell, so BC_UI's vertical bar is
      // deliberately empty here — AppComponent hides it on `menu.length`. Emitting is what matters:
      // falling through with no branch leaves `menu` holding the PREVIOUS module's items, so
      // switching in from Finance showed Finance's sidebar next to Custodis's own.
      this.changeMenu.emit([]);
    }
  }

  /**
   * Open the selected organization in a NEW browser tab (`/?org=<companyId>`) instead of
   * switching this tab. Each tab keeps its own active company + permissions (per-tab
   * OrgScopeStore seeded from the `?org=` param on load), so any number of organizations
   * can be worked on side by side without clobbering each other. This tab is untouched.
   *
   * `module: 1` (Home) is pinned so the new tab always lands on the dashboard. Without it
   * HomeComponent falls back to the persisted module — and sessionStorage is cloned into a
   * tab opened via window.open, so the new organization would inherit whatever module this
   * tab is in and immediately redirect off home into that module's workspace (Accounting,
   * Inventory, Tax, HR, Reporting). If the user has no Home access in the target company,
   * HomeComponent's existing fallback picks their first accessible module.
   */
  switchToCompany(companyId: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!companyId) return;
    // One Workspace: the switch happens in this tab, in place (OrgSwitchService), on the page that
    // is open — no new tab, no reload. Ctrl/Cmd/middle-click still opens it in a new tab.
    const switcher = orgSwitcher();
    if (switcher && !(event.ctrlKey || event.metaKey || event.button === 1)) {
      switcher.switchTo(companyId);
      return;
    }
    const urlTree = this.router.createUrlTree(['/'], { queryParams: { org: companyId, module: 1, [ORG_ARRIVAL_PARAM]: 1 } });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  onCompanySearch(value: string) {
    if (value !== '') {
      this.filteredCompanies = this.userCompanies
        .filter(company => company.name.toLowerCase().includes(value.toLowerCase()))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    } else {
      this.filteredCompanies = this.userCompanies;
    }
  }

  onLogout() {
    this.logout.emit();
  }

  getNavItemHref(item: any): string {
    if (!item.children || item.children.length === 0) {
      return this.buildNavHref(item);
    }
    return null;
  }

  buildNavHref(item: any): string {
    let route = '/' + item.route;
    const params: string[] = [];
    if (item.queryParam !== null && item.queryParam !== undefined) {
      params.push(`type=${item.queryParam}`);
    }
    if (item.queryParams) {
      Object.entries(item.queryParams).forEach(([key, value]) => {
        params.push(`${key}=${value}`);
      });
    }
    return params.length > 0 ? `${route}?${params.join('&')}` : route;
  }

  onNavItemClick(item: any, event?: MouseEvent): void {
    if (!item.children || item.children.length === 0) {
      // Allow ctrl+click / middle-click to open in new tab natively
      if (event && (event.ctrlKey || event.metaKey || event.button === 1)) {
        return;
      }
      event?.preventDefault();
      this.activeNavItem = item.name;
      this.modulesService.setModuleName(item.name);
      this.router.navigate(['/' + item.route]);
      this.navDropdownOpen = false;
    } else {
      // Parent items with children - just open the dropdown, don't navigate
      event?.preventDefault();
      if (this.navDropdownOpen && this.navDropdownItem?.name === item.name) {
        this.navDropdownOpen = false;
        this.navDropdownItem = null;
      } else {
        this.navDropdownOpen = true;
        this.navDropdownItem = item;
      }
    }
  }

  onNavChildClick(child: any, event?: MouseEvent): void {
    // Allow ctrl+click / middle-click to open in new tab natively
    if (event && (event.ctrlKey || event.metaKey || event.button === 1)) {
      return;
    }
    event?.preventDefault();

    this.navDropdownOpen = false;
    this.navDropdownItem = null;

    // If on the dashboard, open the corresponding tab instead of navigating away
    if (this.isOnDashboard()) {
      const route = child.route;
      const mapping = this.dashboardRouteMap[route];
      if (mapping) {
        this.router.navigate(['/invoicing-dashboard'], {
          queryParams: mapping.subTab !== undefined
            ? { view: mapping.view, tab: mapping.tab, subTab: mapping.subTab }
            : { view: mapping.view, tab: mapping.tab }
        });
        return;
      }
    }

    if (child.children && child.children.length > 0) {
      // Navigate to first sub-child
      const firstChild = child.children[0];
      this.router.navigate(['/' + firstChild.route], firstChild.queryParams ? { queryParams: firstChild.queryParams } : {});
    } else {
      if (child.queryParam !== null && child.queryParam !== undefined) {
        this.router.navigate(['/' + child.route], { queryParams: { type: child.queryParam } });
      } else if (child.queryParams) {
        this.router.navigate(['/' + child.route], { queryParams: child.queryParams });
      } else {
        this.router.navigate(['/' + child.route]);
      }
    }
  }

  private isOnDashboard(): boolean {
    return this.router.url.startsWith('/invoicing-dashboard');
  }

  isNavItemActive(item: any): boolean {
    const path = this.router.url.split('?')[0];

    // A route matches when the current path equals it or is nested under it — segment-aware, so
    // 'hr' does NOT match '/hr/salary-details' (which caused Employees to light up everywhere).
    const matches = (route: string): boolean => {
      if (!route) return false;
      const r = '/' + route.replace(/^\/+/, '');
      return path === r || path.startsWith(r + '/');
    };

    // All routes an item covers (itself + any descendants).
    const routesOf = (node: any): string[] => {
      const routes: string[] = [];
      const walk = (n: any) => {
        if (n?.route) routes.push(n.route);
        (n?.children || []).forEach(walk);
      };
      walk(node);
      return routes;
    };

    // Most-specific wins: only the item whose matching route is the longest is "active", so a
    // parent-path item (Employees → 'hr') never steals the highlight from a deeper page.
    const matchingLengths = (this.menu || [])
      .flatMap(routesOf)
      .filter(matches)
      .map(r => r.length);
    if (!matchingLengths.length) return false;
    const best = Math.max(...matchingLengths);

    return routesOf(item).some(r => matches(r) && r.length === best);
  }

  onClose(event) {
    this.filteredCompanies = !event ? this.userCompanies : this.filteredCompanies;
    this.companyFilter.nativeElement.value = '';
  }

  /**
   * Notes opens the full page in a new window.
   *
   * This button used to toggle the side panel. toggleMeetingNotesPanel below is kept and still
   * works, but nothing calls it any more — this was the panel's only entry point, so restoring
   * it is a one-line change here rather than a rebuild.
   *
   * The unread badge is unaffected: it reads MeetingNotesPanelService's count, which is
   * maintained whether or not the panel is ever opened.
   */
  openTaggedNotes(event: MouseEvent): void {
    event.stopPropagation();
    const urlTree = this.router.createUrlTree(['/tagged-notes']);
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  toggleMeetingNotesPanel(): void {
    this.meetingNotesPanelService.togglePanel();
  }

  toggleAiChat(): void {
    this.aiChatPanelService.toggle();
  }

  toggleOutlookPanel(): void {
    this.outlookPanelService.toggle();
  }

  /** Href used by the Contacts anchor so Ctrl/⌘/middle-click open the real new tab. */
  get phonebookUrl(): string {
    return this.router.serializeUrl(this.router.createUrlTree(['/phonebook']));
  }

  /**
   * Contacts header action. A plain click toggles the slide-in phonebook
   * panel (how it worked before the standalone page existed). Modifier or
   * middle clicks fall through to the anchor's href so the browser opens the
   * full /phonebook page in a new tab/window.
   */
  onContactsClick(event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button === 1) {
      event.stopPropagation();
      return; // let the browser handle the new tab/window
    }
    event.preventDefault();
    event.stopPropagation();
    this.phonebookSidebarService.toggle();
  }

  /** Open the contacts/phonebook as a standalone full page in a new browser tab. */
  openPhonebookTab(): void {
    window.open(this.phonebookUrl, '_blank');
  }

  /** Open the "new contact" dialog from anywhere in the app. */
  addContact(): void {
    this.phonebookSidebarService.requestCreate();
  }

  private redirectToMeetingNote(note: NotificationVM) {
    // This tab's organisation: the thread loads under the note's own company wherever it opens.
    // noteId is the thread (the page selects threads); targetNoteId the reply to scroll to.
    const root = note.rootMeetingNoteId || note.meetingNoteId;
    openInOrgTab(this.router, '/tagged-notes', {
      noteId: root,
      formId: note.submittedFormId,
      ...(note.meetingNoteId && note.meetingNoteId !== root ? { targetNoteId: note.meetingNoteId } : {}),
    }, null);
  }

  private startNotificationPolling(): void {
    if (this.isPolling) return;
    this.isPolling = true;

    // Preferences decide what the presenter is allowed to surface, so load them before the first
    // push can arrive rather than lazily on the first notification.
    this.notificationPreferences.load().pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(preferences => {
      // Push is a per-DEVICE registration but the preference is per-user, so a browser the user has
      // not enabled push on yet has nothing to deliver to even though their settings say it is on.
      // Re-register silently wherever the browser has already granted permission — that covers a
      // second browser on the same machine, and a subscription the browser rotated or expired.
      if (preferences.some(p => p.push)) {
        void this.webPush.ensureSubscribed();
      }
    });

    // Primary path: the server tells us when this user's notifications changed.
    this.realtime.on(RealtimeEvent.NotificationsChanged).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => this.refreshNotifications());

    // Fallback only. While the hub is connected this fires but skips, so the steady state is zero
    // requests instead of one every 10s. If the hub is down (WebSockets blocked, API restarting)
    // we still refresh, just slowly, rather than freezing the badge.
    interval(this.FALLBACK_POLLING_INTERVAL).pipe(
      filter(() => !this.realtime.isConnected),
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => this.refreshNotifications());
  }

  private refreshNotifications(): void {
    this.formsService.getAllNotifications().pipe(
      catchError((err) => {
        console.error('Error refreshing notifications:', err);
        return of([]);
      })
    ).subscribe((rawNotifications) => {
      // Only refresh on a real change, so an unchanged list costs no re-render.
      if (!this.hasNotificationsChanged(rawNotifications)) return;

      // Surface anything new on screen before re-rendering. Fed from here rather than fetching
      // again in the presenter, so a push still costs exactly one request.
      this.notificationPresenter.present(rawNotifications.map(n => enrichNotification(n)));

      // Always publish a NEW array reference. app-notification-dropdown rebuilds its own view
      // arrays in ngOnChanges, which Angular only fires when the @Input reference changes — the
      // previous "update in place to preserve UI state" branch mutated the same array, so any
      // change that kept the id set identical (isRead flipped elsewhere, an edited message) never
      // reached the open panel. The dropdown copies out of this array anyway, so preserving object
      // identity bought nothing. The push makes this obvious: updates now land instantly and a
      // stale panel reads as "SignalR isn't working".
      this.notifications = rawNotifications.map(n => enrichNotification(n));
    });
  }

  private hasNotificationsChanged(newNotifications: any[]): boolean {
    if (this.notifications.length !== newNotifications.length) return true;

    for (const newNotif of newNotifications) {
      const existing = this.notifications.find(n => n.id === newNotif.id);
      if (!existing) return true;
      if (existing.isRead !== newNotif.isRead ||
          existing.message !== newNotif.message) {
        return true;
      }
    }
    return false;
  }
}
